import type { SpecialCategory } from './classify';

// Shapes shared by the public catalog reader (server) and the catalog browser
// (client). Kept in their own file because public-catalog.ts is marked
// 'server-only' and a client component may not import from it, not even for a
// type in some build configurations.

/**
 * The special-item categories, the same four the signed-in Miniatures and
 * Samples page splits on. Aliased from the classifier rather than retyped, so
 * a fifth category added there breaks this file loudly instead of silently
 * dropping out of the public filter.
 */
export type CatalogCategory = SpecialCategory;

export const CATALOG_CATEGORIES: readonly CatalogCategory[] = [
  'miniature',
  'sample',
  'vial',
  'gwp',
];

/** Chip labels. Same wording as the signed-in Specials tabs. */
export const CATEGORY_LABEL: Record<CatalogCategory, string> = {
  miniature: 'Miniatures',
  sample: 'Samples',
  vial: 'Vials',
  gwp: 'Gift items',
};

/** Singular tag shown on a row. */
export const CATEGORY_TAG: Record<CatalogCategory, string> = {
  miniature: 'Miniature',
  sample: 'Sample',
  vial: 'Vial',
  gwp: 'Gift',
};

/** Lower-case singular, for the one-result case. */
export const CATEGORY_NOUN_ONE: Record<CatalogCategory, string> = {
  miniature: 'miniature',
  sample: 'sample',
  vial: 'vial',
  gwp: 'gift item',
};

/** Lower-case plural, for "Showing 120 of 900 miniatures". */
export const CATEGORY_NOUN: Record<CatalogCategory, string> = {
  miniature: 'miniatures',
  sample: 'samples',
  vial: 'vials',
  gwp: 'gift items',
};

export interface CatalogEntry {
  /** The product name, cleaned for display. */
  name: string;
  /** null for an ordinary full-size product. */
  category: CatalogCategory | null;
}

/** How many of each category match the current search, ignoring the filter. */
export interface CatalogCounts {
  all: number;
  miniature: number;
  sample: number;
  vial: number;
  gwp: number;
}

/**
 * A ?cat= value from the URL, or null. Anything unrecognised is null rather
 * than an error: a filter is a view, and a junk value should show the whole
 * catalog rather than a failure.
 */
export function parseCatalogCategory(value: string | null | undefined): CatalogCategory | null {
  if (!value) return null;
  return (CATALOG_CATEGORIES as readonly string[]).includes(value)
    ? (value as CatalogCategory)
    : null;
}
